import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Volume2, Play, Pause, Star, Music, Book, Calculator, Mic, Eye, BarChart3, CheckCircle2, Trophy, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { Switch } from '../components/ui/switch';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import confetti from 'canvas-confetti';
import VoiceSelector, { VOICE_OPTIONS } from '../components/VoiceSelector';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Alphabet data with words and images
const ALPHABET_DATA = [
  { letter: 'A', word: 'Apple', emoji: '🍎', color: 'bg-red-100 border-red-300' },
  { letter: 'B', word: 'Ball', emoji: '⚽', color: 'bg-blue-100 border-blue-300' },
  { letter: 'C', word: 'Cat', emoji: '🐱', color: 'bg-orange-100 border-orange-300' },
  { letter: 'D', word: 'Dog', emoji: '🐕', color: 'bg-amber-100 border-amber-300' },
  { letter: 'E', word: 'Elephant', emoji: '🐘', color: 'bg-gray-100 border-gray-300' },
  { letter: 'F', word: 'Fish', emoji: '🐟', color: 'bg-cyan-100 border-cyan-300' },
  { letter: 'G', word: 'Grapes', emoji: '🍇', color: 'bg-purple-100 border-purple-300' },
  { letter: 'H', word: 'House', emoji: '🏠', color: 'bg-yellow-100 border-yellow-300' },
  { letter: 'I', word: 'Ice Cream', emoji: '🍦', color: 'bg-pink-100 border-pink-300' },
  { letter: 'J', word: 'Juice', emoji: '🧃', color: 'bg-orange-100 border-orange-300' },
  { letter: 'K', word: 'Kite', emoji: '🪁', color: 'bg-sky-100 border-sky-300' },
  { letter: 'L', word: 'Lion', emoji: '🦁', color: 'bg-amber-100 border-amber-300' },
  { letter: 'M', word: 'Moon', emoji: '🌙', color: 'bg-indigo-100 border-indigo-300' },
  { letter: 'N', word: 'Nest', emoji: '🪹', color: 'bg-amber-100 border-amber-300' },
  { letter: 'O', word: 'Orange', emoji: '🍊', color: 'bg-orange-100 border-orange-300' },
  { letter: 'P', word: 'Penguin', emoji: '🐧', color: 'bg-slate-100 border-slate-300' },
  { letter: 'Q', word: 'Queen', emoji: '👑', color: 'bg-yellow-100 border-yellow-300' },
  { letter: 'R', word: 'Rainbow', emoji: '🌈', color: 'bg-pink-100 border-pink-300' },
  { letter: 'S', word: 'Sun', emoji: '☀️', color: 'bg-yellow-100 border-yellow-300' },
  { letter: 'T', word: 'Tree', emoji: '🌳', color: 'bg-green-100 border-green-300' },
  { letter: 'U', word: 'Umbrella', emoji: '☂️', color: 'bg-blue-100 border-blue-300' },
  { letter: 'V', word: 'Violin', emoji: '🎻', color: 'bg-amber-100 border-amber-300' },
  { letter: 'W', word: 'Whale', emoji: '🐋', color: 'bg-blue-100 border-blue-300' },
  { letter: 'X', word: 'Xylophone', emoji: '🎹', color: 'bg-pink-100 border-pink-300' },
  { letter: 'Y', word: 'Yo-yo', emoji: '🪀', color: 'bg-red-100 border-red-300' },
  { letter: 'Z', word: 'Zebra', emoji: '🦓', color: 'bg-slate-100 border-slate-300' },
];

// Counting data with emojis
const COUNTING_DATA = [
  { number: 1, word: 'One', emoji: '⭐', items: ['⭐'] },
  { number: 2, word: 'Two', emoji: '🍎', items: ['🍎', '🍎'] },
  { number: 3, word: 'Three', emoji: '🐱', items: ['🐱', '🐱', '🐱'] },
  { number: 4, word: 'Four', emoji: '🌸', items: ['🌸', '🌸', '🌸', '🌸'] },
  { number: 5, word: 'Five', emoji: '🐟', items: ['🐟', '🐟', '🐟', '🐟', '🐟'] },
  { number: 6, word: 'Six', emoji: '🦋', items: ['🦋', '🦋', '🦋', '🦋', '🦋', '🦋'] },
  { number: 7, word: 'Seven', emoji: '🌈', items: ['🌈', '🌈', '🌈', '🌈', '🌈', '🌈', '🌈'] },
  { number: 8, word: 'Eight', emoji: '🎈', items: ['🎈', '🎈', '🎈', '🎈', '🎈', '🎈', '🎈', '🎈'] },
  { number: 9, word: 'Nine', emoji: '🍪', items: ['🍪', '🍪', '🍪', '🍪', '🍪', '🍪', '🍪', '🍪', '🍪'] },
  { number: 10, word: 'Ten', emoji: '🌟', items: ['🌟', '🌟', '🌟', '🌟', '🌟', '🌟', '🌟', '🌟', '🌟', '🌟'] },
  // Extended for Kindergarten
  { number: 11, word: 'Eleven', emoji: '🍓', items: Array(11).fill('🍓') },
  { number: 12, word: 'Twelve', emoji: '🌺', items: Array(12).fill('🌺') },
  { number: 13, word: 'Thirteen', emoji: '🐝', items: Array(13).fill('🐝') },
  { number: 14, word: 'Fourteen', emoji: '🎀', items: Array(14).fill('🎀') },
  { number: 15, word: 'Fifteen', emoji: '🍀', items: Array(15).fill('🍀') },
  { number: 16, word: 'Sixteen', emoji: '💎', items: Array(16).fill('💎') },
  { number: 17, word: 'Seventeen', emoji: '🌻', items: Array(17).fill('🌻') },
  { number: 18, word: 'Eighteen', emoji: '🍬', items: Array(18).fill('🍬') },
  { number: 19, word: 'Nineteen', emoji: '🎯', items: Array(19).fill('🎯') },
  { number: 20, word: 'Twenty', emoji: '🎉', items: Array(20).fill('🎉') },
];

// Phonics data with mouth shape descriptions
const PHONICS_DATA = [
  { sound: 'A', phoneme: '/æ/', word: 'Apple', mouthDesc: 'Open mouth wide, tongue flat', mouthImage: 'https://static.prod-images.emergentagent.com/jobs/604ee634-d3c1-4dbd-a232-c16bbc994be9/images/369050cf2437bd16999807490e22e3642412c162325dcca4d322299b3c720543.png' },
  { sound: 'E', phoneme: '/ɛ/', word: 'Egg', mouthDesc: 'Mouth slightly open, tongue mid-front', mouthImage: null },
  { sound: 'I', phoneme: '/ɪ/', word: 'Igloo', mouthDesc: 'Smile slightly, tongue high front', mouthImage: null },
  { sound: 'O', phoneme: '/ɒ/', word: 'Octopus', mouthDesc: 'Round lips, mouth open', mouthImage: null },
  { sound: 'U', phoneme: '/ʌ/', word: 'Umbrella', mouthDesc: 'Relaxed mouth, tongue mid-back', mouthImage: null },
  { sound: 'B', phoneme: '/b/', word: 'Ball', mouthDesc: 'Press lips together, then pop open', mouthImage: null },
  { sound: 'C', phoneme: '/k/', word: 'Cat', mouthDesc: 'Back of tongue touches roof', mouthImage: null },
  { sound: 'D', phoneme: '/d/', word: 'Dog', mouthDesc: 'Tongue tip touches ridge behind teeth', mouthImage: null },
  { sound: 'F', phoneme: '/f/', word: 'Fish', mouthDesc: 'Top teeth on lower lip, blow air', mouthImage: null },
  { sound: 'G', phoneme: '/g/', word: 'Go', mouthDesc: 'Back of tongue touches soft palate', mouthImage: null },
  { sound: 'H', phoneme: '/h/', word: 'Hat', mouthDesc: 'Mouth open, gentle breath out', mouthImage: null },
  { sound: 'J', phoneme: '/dʒ/', word: 'Jump', mouthDesc: 'Tongue behind teeth, then release', mouthImage: null },
  { sound: 'K', phoneme: '/k/', word: 'Kite', mouthDesc: 'Back of tongue against soft palate', mouthImage: null },
  { sound: 'L', phoneme: '/l/', word: 'Lion', mouthDesc: 'Tongue tip touches ridge, voice on', mouthImage: null },
  { sound: 'M', phoneme: '/m/', word: 'Moon', mouthDesc: 'Lips closed, hum through nose', mouthImage: null },
  { sound: 'N', phoneme: '/n/', word: 'Nest', mouthDesc: 'Tongue tip on ridge, hum through nose', mouthImage: null },
  { sound: 'P', phoneme: '/p/', word: 'Pig', mouthDesc: 'Press lips, pop air out', mouthImage: null },
  { sound: 'R', phoneme: '/r/', word: 'Rain', mouthDesc: 'Curl tongue back, lips rounded', mouthImage: null },
  { sound: 'S', phoneme: '/s/', word: 'Sun', mouthDesc: 'Teeth together, hiss like snake', mouthImage: null },
  { sound: 'T', phoneme: '/t/', word: 'Tree', mouthDesc: 'Tongue tip taps ridge quickly', mouthImage: null },
  { sound: 'V', phoneme: '/v/', word: 'Van', mouthDesc: 'Top teeth on lower lip, voice on', mouthImage: null },
  { sound: 'W', phoneme: '/w/', word: 'Water', mouthDesc: 'Round lips tight, then relax', mouthImage: null },
  { sound: 'X', phoneme: '/ks/', word: 'Box', mouthDesc: 'K sound then S sound together', mouthImage: null },
  { sound: 'Y', phoneme: '/j/', word: 'Yellow', mouthDesc: 'Tongue high, slide to next vowel', mouthImage: null },
  { sound: 'Z', phoneme: '/z/', word: 'Zebra', mouthDesc: 'Teeth together, buzz like bee', mouthImage: null },
];

const EarlyLearning = () => {
  const { childId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [child, setChild] = useState(null);
  const [activeTab, setActiveTab] = useState('alphabets');
  const [selectedLetter, setSelectedLetter] = useState(null);
  const [selectedNumber, setSelectedNumber] = useState(null);
  const [selectedPhonics, setSelectedPhonics] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentABCIndex, setCurrentABCIndex] = useState(0);
  const [ageGroup, setAgeGroup] = useState('prek'); // prek or kindergarten
  const [parentMode, setParentMode] = useState(false);
  const [progress, setProgress] = useState(null);
  const [practicedItems, setPracticedItems] = useState({ letters: new Set(), numbers: new Set(), phonics: new Set() });
  const [practiceCounts, setPracticeCounts] = useState({ letters: {}, numbers: {}, phonics: {} }); // Track counts for mastery
  const MASTERY_THRESHOLD = 3; // Practice 3 times to master
  const audioRef = useRef(null);
  const abcIntervalRef = useRef(null);
  // TTS Settings - user controllable
  const [ttsVoice, setTtsVoice] = useState('shimmer'); // shimmer or nova
  const [ttsSpeed, setTtsSpeed] = useState(0.8); // Slower default for children

  const api = axios.create({
    baseURL: API_URL,
    headers: { Authorization: `Bearer ${token}` }
  });

  useEffect(() => {
    fetchChild();
    fetchProgress();
    return () => {
      if (abcIntervalRef.current) clearInterval(abcIntervalRef.current);
    };
  }, [childId]);

  const fetchChild = async () => {
    try {
      const response = await api.get(`/api/children/${childId}`);
      setChild(response.data);
      // Set age group based on child's age
      if (['infant', 'toddler', 'preschool'].includes(response.data.age_group)) {
        setAgeGroup('prek');
      } else {
        setAgeGroup('kindergarten');
      }
    } catch (error) {
      console.error('Failed to fetch child:', error);
    }
  };

  const fetchProgress = async () => {
    try {
      const response = await api.get(`/api/early-learning/progress/${childId}`);
      setProgress(response.data);
      // Build sets of practiced items for quick lookup
      const letters = new Set(response.data.letters.practiced.map(p => p.value));
      const numbers = new Set(response.data.numbers.practiced.map(p => p.value));
      const phonics = new Set(response.data.phonics.practiced.map(p => p.value));
      setPracticedItems({ letters, numbers, phonics });
      
      // Build practice counts for mastery tracking
      const letterCounts = {};
      const numberCounts = {};
      const phonicsCounts = {};
      response.data.letters.practiced.forEach(p => letterCounts[p.value] = p.count);
      response.data.numbers.practiced.forEach(p => numberCounts[p.value] = p.count);
      response.data.phonics.practiced.forEach(p => phonicsCounts[p.value] = p.count);
      setPracticeCounts({ letters: letterCounts, numbers: numberCounts, phonics: phonicsCounts });
    } catch (error) {
      console.error('Failed to fetch progress:', error);
    }
  };

  const recordPractice = async (itemType, itemValue) => {
    try {
      await api.post('/api/early-learning/practice', {
        child_id: childId,
        item_type: itemType,
        item_value: itemValue
      });
      
      // Map itemType to the correct key in state
      const stateKey = itemType === 'letter' ? 'letters' : itemType === 'number' ? 'numbers' : 'phonics';
      
      // Get current count before update
      const currentCount = practiceCounts[stateKey]?.[itemValue] || 0;
      const newCount = currentCount + 1;
      
      // Update local state immediately for UI feedback
      setPracticedItems(prev => ({
        ...prev,
        [stateKey]: new Set([...prev[stateKey], itemValue])
      }));
      // Update practice counts for mastery
      setPracticeCounts(prev => ({
        ...prev,
        [stateKey]: {
          ...prev[stateKey],
          [itemValue]: newCount
        }
      }));
      
      // Celebrate when reaching mastery threshold!
      if (newCount === MASTERY_THRESHOLD) {
        celebrateMastery(itemType, itemValue);
      }
    } catch (error) {
      console.error('Failed to record practice:', error);
    }
  };

  // Helper to check if item is mastered (practiced 3+ times)
  const isMastered = (type, value) => {
    return (practiceCounts[type]?.[value] || 0) >= MASTERY_THRESHOLD;
  };

  // Helper to get practice count
  const getPracticeCount = (type, value) => {
    return practiceCounts[type]?.[value] || 0;
  };

  // Calculate mastery stats
  const getMasteryStats = () => {
    if (!progress) return { letters: 0, numbers: 0, phonics: 0 };
    const lettersMastered = progress.letters.practiced.filter(p => p.count >= MASTERY_THRESHOLD).length;
    const numbersMastered = progress.numbers.practiced.filter(p => p.count >= MASTERY_THRESHOLD).length;
    const phonicsMastered = progress.phonics.practiced.filter(p => p.count >= MASTERY_THRESHOLD).length;
    return { letters: lettersMastered, numbers: numbersMastered, phonics: phonicsMastered };
  };

  // Celebrate mastery with confetti!
  const celebrateMastery = (itemType, itemValue) => {
    // Fire confetti celebration
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 },
      colors: ['#FFD700', '#FFA500', '#FF6347', '#FFB6C1', '#87CEEB']
    });

    // Add some sparkles on the sides
    setTimeout(() => {
      confetti({
        particleCount: 50,
        angle: 60,
        spread: 55,
        origin: { x: 0 },
        colors: ['#FFD700', '#FFA500']
      });
      confetti({
        particleCount: 50,
        angle: 120,
        spread: 55,
        origin: { x: 1 },
        colors: ['#FFD700', '#FFA500']
      });
    }, 250);

    // Show celebration toast
    const messages = [
      `🎉 Amazing! You mastered "${itemValue}"!`,
      `⭐ Gold star! "${itemValue}" is now mastered!`,
      `🏆 Super job! You're a "${itemValue}" master now!`,
      `✨ Incredible! "${itemValue}" complete!`
    ];
    toast.success(messages[Math.floor(Math.random() * messages.length)]);
  };

  const speakText = async (text, speedOverride = null) => {
    const speed = speedOverride !== null ? speedOverride : ttsSpeed;

    if (!token) {
      console.warn('No auth token, TTS unavailable');
      return;
    }

    try {
      const response = await axios.post(
        `${API_URL}/api/voice/speak?text=${encodeURIComponent(text)}&voice=${ttsVoice}&speed=${speed}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      const audioUrl = URL.createObjectURL(response.data);
      if (audioRef.current) {
        audioRef.current.src = audioUrl;
        await audioRef.current.play().catch(err => {
          console.error('Audio play error:', err);
        });
      }
    } catch (error) {
      console.error('TTS error:', error);
    }
  };

  const handleLetterClick = (item) => {
    setSelectedLetter(item);
    speakText(`${item.letter}. ${item.letter} is for ${item.word}.`);
    recordPractice('letter', item.letter);
  };

  const handleNumberClick = (item) => {
    setSelectedNumber(item);
    speakText(`${item.number}. ${item.word}. Let's count! ${Array(item.number).fill(0).map((_, i) => i + 1).join(', ')}.`);
    recordPractice('number', String(item.number));
  };

  const handlePhonicsClick = (item) => {
    setSelectedPhonics(item);
    speakText(`The letter ${item.sound} makes the sound ${item.phoneme}. ${item.sound} is for ${item.word}. ${item.word}.`);
    recordPractice('phonics', item.sound);
  };

  const playABCSong = () => {
    if (isPlaying) {
      setIsPlaying(false);
      if (abcIntervalRef.current) clearInterval(abcIntervalRef.current);
      return;
    }

    setIsPlaying(true);
    setCurrentABCIndex(0);

    // Play each letter with a pause
    let index = 0;
    const playNextLetter = async () => {
      if (index >= ALPHABET_DATA.length) {
        // Song finished, play ending
        await speakText('Now I know my A B Cs, next time won\'t you sing with me!', 0.9);
        setIsPlaying(false);
        setCurrentABCIndex(0);
        return;
      }

      setCurrentABCIndex(index);
      await speakText(ALPHABET_DATA[index].letter, 1.0);
      index++;
      
      if (index < ALPHABET_DATA.length) {
        abcIntervalRef.current = setTimeout(playNextLetter, 600);
      } else {
        abcIntervalRef.current = setTimeout(playNextLetter, 1500);
      }
    };

    playNextLetter();
  };

  const stopABCSong = () => {
    setIsPlaying(false);
    if (abcIntervalRef.current) {
      clearTimeout(abcIntervalRef.current);
    }
  };

  const maxNumbers = ageGroup === 'prek' ? 10 : 20;
  const showLowercase = ageGroup === 'kindergarten';

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-orange-50">
      {/* Hidden audio element for TTS */}
      <audio ref={audioRef} />

      {/* Header */}
      <div className="bg-gradient-to-r from-amber-400 via-orange-400 to-pink-400 text-white p-6 pb-8 rounded-b-3xl shadow-lg">
        <div className="max-w-6xl mx-auto">
          <Button 
            variant="ghost" 
            className="text-white hover:bg-white/20 mb-4"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back
          </Button>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold mb-2">
                Early Learning
              </h1>
              <p className="text-amber-100 text-lg">
                {child?.name ? `Let's learn, ${child.name}!` : 'Interactive learning for little ones'}
              </p>
            </div>
            <div className="hidden md:flex items-center gap-4">
              <div className="text-center bg-white/20 rounded-2xl px-6 py-3">
                <div className="text-3xl mb-1">🎨</div>
                <div className="text-sm font-medium">
                  {ageGroup === 'prek' ? 'Pre-K' : 'Kindergarten'}
                </div>
              </div>
            </div>
          </div>

          {/* Age Group Toggle */}
          <div className="flex flex-wrap items-center gap-4 mt-4">
            <div className="flex gap-2">
              <Button 
                variant={ageGroup === 'prek' ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => setAgeGroup('prek')}
                className={ageGroup === 'prek' ? 'bg-white text-orange-600' : 'text-white hover:bg-white/20'}
              >
                Pre-K (3-5)
              </Button>
              <Button 
                variant={ageGroup === 'kindergarten' ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => setAgeGroup('kindergarten')}
                className={ageGroup === 'kindergarten' ? 'bg-white text-orange-600' : 'text-white hover:bg-white/20'}
              >
                Kindergarten (5-6)
              </Button>
            </div>
            
            {/* Parent Mode Toggle */}
            <div className="flex items-center gap-2 bg-white/20 rounded-full px-4 py-2">
              <Eye className="w-4 h-4" />
              <span className="text-sm font-medium">Parent Mode</span>
              <Switch 
                checked={parentMode} 
                onCheckedChange={(checked) => {
                  setParentMode(checked);
                  if (checked) fetchProgress();
                }}
                data-testid="parent-mode-toggle"
              />
            </div>
          </div>
          
          {/* Voice Settings */}
          <div className="mt-4">
            <VoiceSelector
              voice={ttsVoice}
              onVoiceChange={setTtsVoice}
              speed={ttsSpeed}
              onSpeedChange={setTtsSpeed}
              theme="dark"
              className="bg-white/10 border-white/20"
            />
          </div>
        </div>
      </div>

      {/* Parent Mode Stats Dashboard */}
      {parentMode && progress && (
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-4">
          <Card className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-xl">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <BarChart3 className="w-6 h-6" />
                <h2 className="text-xl font-bold">{child?.name}'s Learning Progress</h2>
              </div>
              
              {/* Mastery Stats Row */}
              {(() => {
                const mastery = getMasteryStats();
                const totalMastered = mastery.letters + mastery.numbers + mastery.phonics;
                return totalMastered > 0 ? (
                  <div className="bg-gradient-to-r from-yellow-400 to-amber-500 rounded-xl p-4 mb-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-white/30 rounded-full flex items-center justify-center">
                          <Star className="w-7 h-7 text-white fill-white" />
                        </div>
                        <div>
                          <h3 className="font-bold text-lg">Mastery Achievements!</h3>
                          <p className="text-amber-100 text-sm">Items practiced 3+ times</p>
                        </div>
                      </div>
                      <div className="flex gap-4 text-center">
                        <div>
                          <div className="text-2xl font-bold">{mastery.letters}</div>
                          <div className="text-xs text-amber-100">Letters</div>
                        </div>
                        <div>
                          <div className="text-2xl font-bold">{mastery.numbers}</div>
                          <div className="text-xs text-amber-100">Numbers</div>
                        </div>
                        <div>
                          <div className="text-2xl font-bold">{mastery.phonics}</div>
                          <div className="text-xs text-amber-100">Phonics</div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : null;
              })()}
              
              <div className="grid md:grid-cols-4 gap-4 mb-6">
                {/* Letters Progress */}
                <div className="bg-white/20 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Book className="w-5 h-5" />
                    <span className="font-semibold">Letters</span>
                  </div>
                  <div className="text-3xl font-bold mb-1">{progress.letters.total_practiced}/26</div>
                  <Progress value={progress.letters.completion_percent} className="h-2 bg-white/30" />
                  <p className="text-sm mt-1 text-indigo-100">{progress.letters.completion_percent}% complete</p>
                  <p className="text-xs text-indigo-200 mt-1">⭐ {getMasteryStats().letters} mastered</p>
                </div>
                
                {/* Numbers Progress */}
                <div className="bg-white/20 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Calculator className="w-5 h-5" />
                    <span className="font-semibold">Numbers</span>
                  </div>
                  <div className="text-3xl font-bold mb-1">{progress.numbers.total_practiced}/20</div>
                  <Progress value={progress.numbers.completion_percent} className="h-2 bg-white/30" />
                  <p className="text-sm mt-1 text-indigo-100">{progress.numbers.completion_percent}% complete</p>
                  <p className="text-xs text-indigo-200 mt-1">⭐ {getMasteryStats().numbers} mastered</p>
                </div>
                
                {/* Phonics Progress */}
                <div className="bg-white/20 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Mic className="w-5 h-5" />
                    <span className="font-semibold">Phonics</span>
                  </div>
                  <div className="text-3xl font-bold mb-1">{progress.phonics.total_practiced}/26</div>
                  <Progress value={progress.phonics.completion_percent} className="h-2 bg-white/30" />
                  <p className="text-sm mt-1 text-indigo-100">{progress.phonics.completion_percent}% complete</p>
                  <p className="text-xs text-indigo-200 mt-1">⭐ {getMasteryStats().phonics} mastered</p>
                </div>
                
                {/* Total Stats */}
                <div className="bg-white/20 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Trophy className="w-5 h-5" />
                    <span className="font-semibold">Stats</span>
                  </div>
                  <div className="text-3xl font-bold mb-1">{progress.stats.total_practice_count}</div>
                  <p className="text-sm text-indigo-100">Total practices</p>
                  {progress.stats.most_practiced_letter && (
                    <p className="text-xs text-indigo-200 mt-1">Favorite: "{progress.stats.most_practiced_letter}"</p>
                  )}
                </div>
              </div>
              
              {/* Progress Legend */}
              <div className="flex flex-wrap items-center gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded bg-yellow-400 flex items-center justify-center">
                    <Star className="w-3 h-3 text-white fill-white" />
                  </div>
                  <span>Mastered (3+ practices)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded bg-green-400 flex items-center justify-center">
                    <CheckCircle2 className="w-3 h-3 text-white" />
                  </div>
                  <span>Practiced</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded bg-white/30"></div>
                  <span>Not yet practiced</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Content */}
      <div className="max-w-6xl mx-auto p-4 md:p-6 -mt-4">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-4 mb-6 bg-white shadow-md rounded-2xl p-1">
            <TabsTrigger value="alphabets" className="rounded-xl data-[state=active]:bg-amber-500 data-[state=active]:text-white">
              <Book className="w-4 h-4 mr-2" />
              ABCs
            </TabsTrigger>
            <TabsTrigger value="counting" className="rounded-xl data-[state=active]:bg-blue-500 data-[state=active]:text-white">
              <Calculator className="w-4 h-4 mr-2" />
              123s
            </TabsTrigger>
            <TabsTrigger value="phonics" className="rounded-xl data-[state=active]:bg-green-500 data-[state=active]:text-white">
              <Mic className="w-4 h-4 mr-2" />
              Phonics
            </TabsTrigger>
            <TabsTrigger value="song" className="rounded-xl data-[state=active]:bg-pink-500 data-[state=active]:text-white">
              <Music className="w-4 h-4 mr-2" />
              Song
            </TabsTrigger>
          </TabsList>

          {/* Alphabets Tab */}
          <TabsContent value="alphabets">
            <Card className="mb-6 bg-gradient-to-r from-amber-100 to-orange-100 border-none">
              <CardContent className="p-6">
                <h2 className="text-2xl font-bold text-amber-800 mb-2">Learn Your ABCs!</h2>
                <p className="text-amber-700">Tap any letter to hear it spoken. Each letter has a fun word!</p>
              </CardContent>
            </Card>

            {/* Selected Letter Display */}
            {selectedLetter && (
              <Card className="mb-6 bg-white shadow-lg border-2 border-amber-300">
                <CardContent className="p-8 text-center">
                  <div className="text-8xl md:text-9xl font-bold text-amber-500 mb-4">
                    {selectedLetter.letter}
                    {showLowercase && <span className="text-6xl md:text-7xl text-amber-400 ml-4">{selectedLetter.letter.toLowerCase()}</span>}
                  </div>
                  <div className="text-6xl mb-4">{selectedLetter.emoji}</div>
                  <div className="text-3xl font-bold text-slate-700">{selectedLetter.letter} is for {selectedLetter.word}</div>
                  <Button 
                    className="mt-4 bg-amber-500 hover:bg-amber-600"
                    onClick={() => speakText(`${selectedLetter.letter}. ${selectedLetter.letter} is for ${selectedLetter.word}.`)}
                  >
                    <Volume2 className="w-5 h-5 mr-2" />
                    Hear Again
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Alphabet Grid */}
            <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-7 lg:grid-cols-9 gap-3">
              {ALPHABET_DATA.map((item) => {
                const isPracticed = practicedItems.letters.has(item.letter);
                const mastered = isMastered('letters', item.letter);
                const count = getPracticeCount('letters', item.letter);
                return (
                  <Card 
                    key={item.letter}
                    className={`cursor-pointer transition-all hover:scale-105 hover:shadow-lg ${item.color} border-2 ${selectedLetter?.letter === item.letter ? 'ring-4 ring-amber-400 scale-105' : ''} ${parentMode && mastered ? 'ring-2 ring-yellow-400' : parentMode && isPracticed ? 'ring-2 ring-green-500' : ''}`}
                    onClick={() => handleLetterClick(item)}
                    data-testid={`letter-${item.letter}`}
                  >
                    <CardContent className="p-3 md:p-4 text-center relative">
                      {parentMode && mastered ? (
                        <div className="absolute -top-1 -right-1 w-6 h-6 bg-yellow-400 rounded-full flex items-center justify-center shadow-md animate-pulse">
                          <Star className="w-4 h-4 text-white fill-white" />
                        </div>
                      ) : parentMode && isPracticed ? (
                        <div className="absolute -top-1 -right-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                          <span className="text-[10px] font-bold text-white">{count}/3</span>
                        </div>
                      ) : null}
                      <div className="text-3xl md:text-4xl font-bold text-slate-700 mb-1">
                        {item.letter}
                        {showLowercase && <span className="text-xl md:text-2xl text-slate-500 ml-1">{item.letter.toLowerCase()}</span>}
                      </div>
                      <div className="text-2xl md:text-3xl">{item.emoji}</div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          {/* Counting Tab */}
          <TabsContent value="counting">
            <Card className="mb-6 bg-gradient-to-r from-blue-100 to-cyan-100 border-none">
              <CardContent className="p-6">
                <h2 className="text-2xl font-bold text-blue-800 mb-2">Let's Count!</h2>
                <p className="text-blue-700">Tap a number to count along. See the items appear!</p>
              </CardContent>
            </Card>

            {/* Selected Number Display */}
            {selectedNumber && (
              <Card className="mb-6 bg-white shadow-lg border-2 border-blue-300">
                <CardContent className="p-8 text-center">
                  <div className="text-8xl md:text-9xl font-bold text-blue-500 mb-4">
                    {selectedNumber.number}
                  </div>
                  <div className="text-3xl font-bold text-slate-700 mb-4">{selectedNumber.word}</div>
                  <div className="flex flex-wrap justify-center gap-2 text-4xl mb-4">
                    {selectedNumber.items.map((emoji, i) => (
                      <span key={i} className="animate-bounce" style={{ animationDelay: `${i * 0.1}s` }}>
                        {emoji}
                      </span>
                    ))}
                  </div>
                  <Button 
                    className="mt-4 bg-blue-500 hover:bg-blue-600"
                    onClick={() => speakText(`${selectedNumber.number}. ${selectedNumber.word}. Let's count! ${Array(selectedNumber.number).fill(0).map((_, i) => i + 1).join(', ')}.`)}
                  >
                    <Volume2 className="w-5 h-5 mr-2" />
                    Count Again
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Number Grid */}
            <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-10 gap-3">
              {COUNTING_DATA.slice(0, maxNumbers).map((item) => {
                const isPracticed = practicedItems.numbers.has(String(item.number));
                const mastered = isMastered('numbers', String(item.number));
                const count = getPracticeCount('numbers', String(item.number));
                return (
                  <Card 
                    key={item.number}
                    className={`cursor-pointer transition-all hover:scale-105 hover:shadow-lg bg-gradient-to-br from-blue-50 to-cyan-50 border-2 border-blue-200 ${selectedNumber?.number === item.number ? 'ring-4 ring-blue-400 scale-105' : ''} ${parentMode && mastered ? 'ring-2 ring-yellow-400' : parentMode && isPracticed ? 'ring-2 ring-green-500' : ''}`}
                    onClick={() => handleNumberClick(item)}
                    data-testid={`number-${item.number}`}
                  >
                    <CardContent className="p-3 md:p-4 text-center relative">
                      {parentMode && mastered ? (
                        <div className="absolute -top-1 -right-1 w-6 h-6 bg-yellow-400 rounded-full flex items-center justify-center shadow-md animate-pulse">
                          <Star className="w-4 h-4 text-white fill-white" />
                        </div>
                      ) : parentMode && isPracticed ? (
                        <div className="absolute -top-1 -right-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                          <span className="text-[10px] font-bold text-white">{count}/3</span>
                        </div>
                      ) : null}
                      <div className="text-3xl md:text-4xl font-bold text-blue-600 mb-1">
                        {item.number}
                      </div>
                      <div className="text-xl md:text-2xl">{item.emoji}</div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          {/* Phonics Tab */}
          <TabsContent value="phonics">
            <Card className="mb-6 bg-gradient-to-r from-green-100 to-emerald-100 border-none">
              <CardContent className="p-6">
                <h2 className="text-2xl font-bold text-green-800 mb-2">Phonics Fun!</h2>
                <p className="text-green-700">Learn how to make each sound. See how your mouth should look!</p>
              </CardContent>
            </Card>

            {/* Selected Phonics Display */}
            {selectedPhonics && (
              <Card className="mb-6 bg-white shadow-lg border-2 border-green-300">
                <CardContent className="p-8">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="text-center">
                      <div className="text-7xl md:text-8xl font-bold text-green-500 mb-4">
                        {selectedPhonics.sound}
                      </div>
                      <div className="text-2xl font-mono text-slate-500 mb-2">{selectedPhonics.phoneme}</div>
                      <div className="text-xl text-slate-700 mb-4">
                        <span className="font-bold">{selectedPhonics.sound}</span> is for <span className="font-bold">{selectedPhonics.word}</span>
                      </div>
                      <Button 
                        className="bg-green-500 hover:bg-green-600"
                        onClick={() => speakText(`The letter ${selectedPhonics.sound} makes the sound ${selectedPhonics.phoneme}. ${selectedPhonics.sound} is for ${selectedPhonics.word}.`)}
                      >
                        <Volume2 className="w-5 h-5 mr-2" />
                        Hear Sound
                      </Button>
                    </div>
                    <div className="bg-green-50 rounded-2xl p-6">
                      <h4 className="font-bold text-green-800 mb-3">How to Make This Sound:</h4>
                      <p className="text-lg text-green-700 mb-4">{selectedPhonics.mouthDesc}</p>
                      {selectedPhonics.mouthImage && (
                        <img 
                          src={selectedPhonics.mouthImage} 
                          alt={`Mouth position for ${selectedPhonics.sound}`}
                          className="w-full max-w-xs mx-auto rounded-xl shadow-md"
                        />
                      )}
                      {!selectedPhonics.mouthImage && (
                        <div className="w-32 h-32 mx-auto bg-green-200 rounded-full flex items-center justify-center">
                          <span className="text-6xl">👄</span>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Phonics Grid */}
            <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-7 lg:grid-cols-9 gap-3">
              {PHONICS_DATA.map((item) => {
                const isPracticed = practicedItems.phonics.has(item.sound);
                const mastered = isMastered('phonics', item.sound);
                const count = getPracticeCount('phonics', item.sound);
                return (
                  <Card 
                    key={item.sound}
                    className={`cursor-pointer transition-all hover:scale-105 hover:shadow-lg bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 ${selectedPhonics?.sound === item.sound ? 'ring-4 ring-green-400 scale-105' : ''} ${parentMode && mastered ? 'ring-2 ring-yellow-400' : parentMode && isPracticed ? 'ring-2 ring-green-500' : ''}`}
                    onClick={() => handlePhonicsClick(item)}
                    data-testid={`phonics-${item.sound}`}
                  >
                    <CardContent className="p-3 md:p-4 text-center relative">
                      {parentMode && mastered ? (
                        <div className="absolute -top-1 -right-1 w-6 h-6 bg-yellow-400 rounded-full flex items-center justify-center shadow-md animate-pulse">
                          <Star className="w-4 h-4 text-white fill-white" />
                        </div>
                      ) : parentMode && isPracticed ? (
                        <div className="absolute -top-1 -right-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                          <span className="text-[10px] font-bold text-white">{count}/3</span>
                        </div>
                      ) : null}
                      <div className="text-3xl md:text-4xl font-bold text-green-600 mb-1">
                        {item.sound}
                      </div>
                      <div className="text-xs text-green-500 font-mono">{item.phoneme}</div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          {/* ABC Song Tab */}
          <TabsContent value="song">
            <Card className="mb-6 bg-gradient-to-r from-pink-100 to-purple-100 border-none">
              <CardContent className="p-6">
                <h2 className="text-2xl font-bold text-pink-800 mb-2">ABC Song!</h2>
                <p className="text-pink-700">Sing along with the alphabet! Each letter lights up as it plays.</p>
              </CardContent>
            </Card>

            {/* Play Button */}
            <Card className="mb-6 bg-white shadow-lg">
              <CardContent className="p-8 text-center">
                <Button 
                  size="lg"
                  className={`text-2xl px-12 py-8 rounded-full ${isPlaying ? 'bg-red-500 hover:bg-red-600' : 'bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600'}`}
                  onClick={isPlaying ? stopABCSong : playABCSong}
                  data-testid="abc-song-play"
                >
                  {isPlaying ? (
                    <>
                      <Pause className="w-8 h-8 mr-3" />
                      Stop Song
                    </>
                  ) : (
                    <>
                      <Play className="w-8 h-8 mr-3" />
                      Play ABC Song
                    </>
                  )}
                </Button>
                
                {isPlaying && (
                  <p className="mt-4 text-lg text-pink-600 animate-pulse">
                    Now singing: {ALPHABET_DATA[currentABCIndex]?.letter}
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Animated Alphabet Display */}
            <div className="grid grid-cols-6 sm:grid-cols-9 md:grid-cols-13 gap-2">
              {ALPHABET_DATA.map((item, index) => (
                <Card 
                  key={item.letter}
                  className={`transition-all duration-300 ${
                    isPlaying && index === currentABCIndex 
                      ? 'bg-gradient-to-br from-pink-400 to-purple-400 text-white scale-125 shadow-xl z-10' 
                      : isPlaying && index < currentABCIndex
                      ? 'bg-pink-100 opacity-60'
                      : 'bg-white'
                  }`}
                >
                  <CardContent className="p-2 md:p-3 text-center">
                    <div className={`text-xl md:text-2xl font-bold ${
                      isPlaying && index === currentABCIndex ? 'text-white' : 'text-pink-600'
                    }`}>
                      {item.letter}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Song Lyrics */}
            <Card className="mt-6 bg-gradient-to-r from-pink-50 to-purple-50">
              <CardContent className="p-6">
                <h3 className="font-bold text-pink-800 mb-4 text-center text-xl">ABC Song Lyrics</h3>
                <div className="text-center text-lg text-pink-700 space-y-2">
                  <p>🎵 A B C D E F G 🎵</p>
                  <p>🎵 H I J K L M N O P 🎵</p>
                  <p>🎵 Q R S T U V 🎵</p>
                  <p>🎵 W X Y and Z 🎵</p>
                  <p className="pt-2 italic">Now I know my ABCs,</p>
                  <p className="italic">Next time won't you sing with me! 🎶</p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default EarlyLearning;
